// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAlert from "components/MDAlert";

import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";
import { useNotification } from "context/NotificationContext";

import { changePassword } from "@shared/api/userInfo";

function ChangePassword(): JSX.Element {
  const { t } = useContext(LanguageContext) as any;
  const { refresh, session, logout } = useSessionContext() as any;
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  
  const handleClick = async () => {
    setErrorMessage("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorMessage(t("Sttting-Password.require"));
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage(t("Sttting-Password.require5"));
      return;
    }
    try {
      if (!session?.access_token) {
        await refresh();
      }

      const updated = await changePassword(session.access_token, newPassword);
      console.log(`change-password::${JSON.stringify(updated)}`);

      showNotification({
        icon: "check_circle",
        title: t("Common.success"),
        message: t("Common.updated-success"),
      }, 2000);
      console.log(`update password success`);

      await logout();
      navigate("/pages/auth/Sign-In", { replace: true });       
    } catch (err) {
      console.error("更新エラー", err);
      showNotification({
        icon: "error",
        title: t("Common.error"),
        message: t("Common.updated-error"),
      }, 2000);
    }
  };

  const passwordRequirements = [
    t("Sttting-Password.require1"),
  ];

  const renderPasswordRequirements = passwordRequirements.map((item, key) => {
    const itemKey = `element-${key}`;
    return (
      <MDBox key={itemKey} component="li" color="text" fontSize="1.25rem" lineHeight={1}>
        <MDTypography variant="button" color="text" fontWeight="regular" verticalAlign="middle">
          {item}
        </MDTypography>
      </MDBox>
    );
  });

  return (
    <Card id="change-password">
      <MDBox p={3}>
        <MDTypography variant="h5">{t("Sttting-Password.title")}</MDTypography>
      </MDBox>
      <MDBox component="form" pb={3} px={3}>
        {errorMessage && (
          <MDAlert color="error" dismissible>
            {errorMessage}
          </MDAlert>
        )}
        <Grid container spacing={3}>
          {/* 1行目: 現在のパスワード */}
          <Grid item xs={12} md={6}>
            <MDInput
              fullWidth
              label={t("Sttting-Password.curent-pass")}
              value={currentPassword}
              onChange={(e: any) => setCurrentPassword(e.target.value)}
              type={showCurrent ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCurrent(!showCurrent)} edge="end">
                      <Icon fontSize="small">{showCurrent ? "visibility" : "visibility_off"}</Icon>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              inputProps={{ autoComplete: "current-password" }}
            />
          </Grid>
          
          {/* 強制改行用のスペーサー（PC表示時のみ有効、スマホ等では非表示） */}
          <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}></Grid>

          {/* 2行目: 新しいパスワード */}
          <Grid item xs={12} md={6}>
            <MDInput
              fullWidth
              label={t("Sttting-Password.new-pass")}
              value={newPassword}
              onChange={(e: any) => setNewPassword(e.target.value)}
              type={showNew ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNew(!showNew)} edge="end">
                      <Icon fontSize="small">{showNew ? "visibility" : "visibility_off"}</Icon>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              inputProps={{ autoComplete: "new-password" }}
            />
          </Grid>

          {/* 2行目: 確認用の新しいパスワード */}
          <Grid item xs={12} md={6}>
            <MDInput
              fullWidth
              label={t("Sttting-Password.confirm")}
              value={confirmPassword}
              onChange={(e: any) => setConfirmPassword(e.target.value)}
              type={showConfirm ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                      <Icon fontSize="small">{showConfirm ? "visibility" : "visibility_off"}</Icon>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              inputProps={{ autoComplete: "new-password" }}
            />
          </Grid>
        </Grid>
        
        <MDBox mt={6} mb={1}>
          <MDTypography variant="h5">{t("Sttting-Password.pw-req")}</MDTypography>
        </MDBox>
        <MDBox mb={1}>
          <MDTypography variant="body2" color="text">
            {t("Sttting-Password.note")}
          </MDTypography>
        </MDBox>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap">
          <MDBox component="ul" m={0} pl={3.25} mb={{ xs: 8, sm: 0 }}>
            {renderPasswordRequirements}
          </MDBox>
          <MDBox ml="auto">
            <MDButton variant="gradient" color="info" size="small" onClick={handleClick}>
              {t("Sttting-Password.update")}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default ChangePassword;